import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type FirestoreSyncStatus = 'ready' | 'syncing' | 'pending' | 'processing' | 'error';

const POLL_INTERVAL_MS = 10_000;
const SYNC_TIMEOUT_MS = 10 * 60 * 1_000; // 10 min — bascule client-side en 'error'
                                         // si le callback Django n'arrive jamais
                                         // (worker Celery crashé, Cloud Run figé…).

@Injectable({ providedIn: 'root' })
export class DynamicSyncService implements OnDestroy {
  private statusByEmail = new Map<number, BehaviorSubject<FirestoreSyncStatus>>();
  private pollSubs = new Map<number, Subscription>();
  private timeoutHandles = new Map<number, ReturnType<typeof setTimeout>>();

  constructor(private http: HttpClient) {}

  getStatus$(emailId: number): Observable<FirestoreSyncStatus> {
    return this.ensureSubject(emailId).asObservable();
  }

  getCurrentStatus(emailId: number): FirestoreSyncStatus {
    return this.ensureSubject(emailId).value;
  }

  setStatus(emailId: number, status: FirestoreSyncStatus): void {
    this.ensureSubject(emailId).next(status);
    if (status === 'ready' || status === 'error') {
      this.stopPolling(emailId);
    }
  }

  startSync(emailId: number): void {
    this.setStatus(emailId, 'syncing');
    this.stopPolling(emailId);

    const sub = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).pipe(
      switchMap(() => this.http.get<{ status: FirestoreSyncStatus }>(
        `${environment.backendUrl}/api/dynamic_content/firestore_status/`,
        { params: { email_id: emailId.toString() } }
      ))
    ).subscribe({
      next: (res) => this.setStatus(emailId, res.status),
      error: () => this.stopPolling(emailId)
    });
    this.pollSubs.set(emailId, sub);

    // Safety net : si la sync ne remonte jamais (worker Celery crashé,
    // callback Cloud Run perdu…), on bascule en 'error' après 10 min
    // pour que le front ne reste pas figé sur le bandeau de sync.
    const handle = setTimeout(() => {
      if (this.getCurrentStatus(emailId) === 'syncing') {
        this.setStatus(emailId, 'error');
      }
    }, SYNC_TIMEOUT_MS);
    this.timeoutHandles.set(emailId, handle);
  }

  private stopPolling(emailId: number): void {
    const existing = this.pollSubs.get(emailId);
    if (existing) {
      existing.unsubscribe();
      this.pollSubs.delete(emailId);
    }
    const handle = this.timeoutHandles.get(emailId);
    if (handle) {
      clearTimeout(handle);
      this.timeoutHandles.delete(emailId);
    }
  }

  private ensureSubject(emailId: number): BehaviorSubject<FirestoreSyncStatus> {
    let subj = this.statusByEmail.get(emailId);
    if (!subj) {
      subj = new BehaviorSubject<FirestoreSyncStatus>('ready');
      this.statusByEmail.set(emailId, subj);
    }
    return subj;
  }

  ngOnDestroy(): void {
    this.pollSubs.forEach(sub => sub.unsubscribe());
    this.pollSubs.clear();
    this.timeoutHandles.forEach(h => clearTimeout(h));
    this.timeoutHandles.clear();
  }
}
